import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import organizationClient from "@apis/organizationClient";
import { useOrg } from "@/contexts/OrgContext";
import { RootState } from "@/types/RootState";

interface CheckPagePermissionResponse {
  orgName?: string;
  requiredFeatures?: string;
}

export interface PagePermissionCheck {
  /** false only when the selected org lacks the page's required feature(s) */
  hasAccess: boolean;
  orgName?: string;
  requiredFeatures: string[];
  orgId: number | null;
}

/**
 * Shared feature-gate check for org-scoped pages: asks the backend whether the
 * currently selected org may access `pageName` (PageName enum + its
 * FeatureKeys). The check only applies for SYSTEM/PARTNER logins browsing with
 * an org selected — NORMAL logins and the "All" scope always resolve to
 * accessible. Presentation is up to the caller (banner, empty state, ...).
 */
const usePagePermissionCheck = (
  pageName?: string,
  onAccessResolved?: (hasAccess: boolean) => void
): PagePermissionCheck => {
  const { orgId } = useOrg();
  const loginInfo = useSelector((state: RootState) => state.user);

  const [orgName, setOrgName] = useState<string>();
  const [requiredFeatures, setRequiredFeatures] = useState<string[]>([]);
  const [hasAccess, setHasAccess] = useState<boolean>(true);

  useEffect(() => {
    if (
      pageName &&
      loginInfo.organisation?.organisationType !== "NORMAL" &&
      orgId != null &&
      orgId !== 0
    ) {
      organizationClient
        .checkPagePermission({
          orgId: orgId,
          pageName: pageName,
        })
        .then(({ data }: { data: CheckPagePermissionResponse }) => {
          if (data.orgName) {
            setOrgName(data.orgName);
            setRequiredFeatures(
              data.requiredFeatures ? [data.requiredFeatures] : []
            );
            setHasAccess(false);
            onAccessResolved?.(false);
          } else {
            setHasAccess(true);
            onAccessResolved?.(true);
          }
        })
        .catch(() => {});
    } else {
      // No check applies (own org, or no org selected) — clear a stale result
      setHasAccess(true);
      onAccessResolved?.(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  return { hasAccess, orgName, requiredFeatures, orgId };
};

export default usePagePermissionCheck;
